import React, { useEffect, useState, useMemo, useRef } from 'react';
import { X, Download, Mail, Sparkles, Image as ImageIcon, Film, Loader2, Reply, FileEdit } from 'lucide-react';
import { fetchEmailDetail, downloadAttachmentBlob } from '../utils/api';
import { htmlToPlain, timeAgo } from '../utils/formatters';
import { notify } from '../utils/notify';

function guessPreviewKind(att) {
  const m = (att.mime_type || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  const fn = (att.filename || '').toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|svg|heic|avif)$/.test(fn)) return 'image';
  if (/\.(mp4|webm|ogg|mov|m4v|mkv)$/.test(fn)) return 'video';
  return 'file';
}

/** Gmail often sends application/octet-stream; browsers need a real type for <video>/<img>. */
function previewMimeType(att, kind) {
  const mime = (att.mime_type || '').trim().toLowerCase();
  if (kind === 'video') {
    if (mime.startsWith('video/') && mime !== 'video/octet-stream') return mime;
    const fn = (att.filename || '').toLowerCase();
    if (fn.endsWith('.webm')) return 'video/webm';
    if (fn.endsWith('.ogg') || fn.endsWith('.ogv')) return 'video/ogg';
    if (fn.endsWith('.mov')) return 'video/quicktime';
    if (fn.endsWith('.mkv')) return 'video/x-matroska';
    if (fn.endsWith('.m4v') || fn.endsWith('.mp4')) return 'video/mp4';
    return 'video/mp4';
  }
  if (kind === 'image') {
    if (mime.startsWith('image/') && mime !== 'image/octet-stream') return mime;
    const fn = (att.filename || '').toLowerCase();
    if (fn.endsWith('.png')) return 'image/png';
    if (fn.endsWith('.gif')) return 'image/gif';
    if (fn.endsWith('.webp')) return 'image/webp';
    if (fn.endsWith('.svg')) return 'image/svg+xml';
    if (fn.endsWith('.bmp')) return 'image/bmp';
    if (fn.endsWith('.jpg') || fn.endsWith('.jpeg')) return 'image/jpeg';
    return 'image/jpeg';
  }
  return 'application/octet-stream';
}

export default function EmailViewerModal({ messageId, folder, onClose, onOpenCompose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  /** @type {Record<string, string>} attachment id -> object URL */
  const [previewUrls, setPreviewUrls] = useState({});
  const [previewErrors, setPreviewErrors] = useState({});
  const [videoPlayErrors, setVideoPlayErrors] = useState({});
  const blobUrlsRef = useRef([]);

  const isSent = folder === 'sent';
  const isDraft = folder === 'drafts';
  const isReceived = !isSent && !isDraft;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await fetchEmailDetail(messageId);
        if (!cancelled) setDetail(d);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load message');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [messageId]);

  useEffect(() => {
    blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    blobUrlsRef.current = [];
    setPreviewErrors({});
    setVideoPlayErrors({});

    if (!detail?.attachments?.length) {
      setPreviewUrls({});
      return;
    }

    let cancelled = false;

    (async () => {
      const next = {};
      const errs = {};
      for (const att of detail.attachments) {
        const kind = guessPreviewKind(att);
        if (kind !== 'image' && kind !== 'video') continue;
        try {
          const raw = await downloadAttachmentBlob(messageId, att, { forPreview: true });
          if (cancelled) return;
          const buf = await raw.arrayBuffer();
          if (cancelled) return;
          const mime = previewMimeType(att, kind);
          const typed = new Blob([buf], { type: mime });
          const url = URL.createObjectURL(typed);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          blobUrlsRef.current.push(url);
          next[att.id] = url;
        } catch {
          errs[att.id] = true;
        }
      }
      if (cancelled) {
        blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
        blobUrlsRef.current = [];
        return;
      }
      setPreviewUrls(next);
      setPreviewErrors(errs);
    })();

    return () => {
      cancelled = true;
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      blobUrlsRef.current = [];
      setPreviewUrls({});
    };
  }, [detail, messageId]);

  const handleDownload = async (att) => {
    setDownloading(att.id);
    try {
      const blob = await downloadAttachmentBlob(messageId, att);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      notify.error('Download failed', { description: e.message });
    } finally {
      setDownloading(null);
    }
  };

  const bodyDisplay =
    detail?.body_text?.trim() ||
    htmlToPlain(detail?.body_html || '') ||
    '(No body text)';

  const toLine = detail?.to?.length ? detail.to.join(', ') : '';

  const openDraftEditor = () => {
    if (!detail || !onOpenCompose || !isDraft) return;
    onOpenCompose({
      draftMessageId: messageId,
      to: toLine,
      cc: (detail.cc || []).join(', '),
      subject: detail.subject || '',
      body: detail.body_text?.trim() || htmlToPlain(detail.body_html || ''),
    });
    onClose();
  };

  const openReply = () => {
    if (!detail || !onOpenCompose || !isReceived) return;
    const replyTo = detail.sender_email || detail.sender || '';
    const subj = detail.subject || '';
    const replySubject = /^re:\s*/i.test(subj) ? subj : (subj ? `Re: ${subj}` : '');
    onOpenCompose({
      draftMessageId: null,
      to: replyTo,
      cc: '',
      subject: replySubject,
      body: '',
    });
    onClose();
  };

  const previewAttachments = useMemo(() => {
    if (!detail?.attachments) return [];
    return detail.attachments.filter((a) => {
      const k = guessPreviewKind(a);
      return k === 'image' || k === 'video';
    });
  }, [detail]);

  const otherAttachments = useMemo(() => {
    if (!detail?.attachments) return [];
    return detail.attachments.filter((a) => {
      const k = guessPreviewKind(a);
      return k === 'file';
    });
  }, [detail]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content email-viewer-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={22} /> {isSent ? 'Sent message' : 'Message'}
          </h2>
          <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {loading && <p style={{ color: 'var(--text-dim)' }}>Loading…</p>}
        {error && <p style={{ color: '#ff6b6b' }}>{error}</p>}

        {!loading && detail && (
          <>
            <div style={{ marginTop: 12, fontSize: '0.9rem', color: 'var(--text-dim)' }}>
              {isSent && toLine && (
                <div>
                  <strong>To:</strong> {toLine}
                </div>
              )}
              {!isSent && (
                <div>
                  <strong>From:</strong> {detail.sender} &lt;{detail.sender_email}&gt;
                </div>
              )}
              {isSent && (
                <div>
                  <strong>From:</strong> {detail.sender}
                </div>
              )}
              {toLine && folder === 'drafts' && (
                <div>
                  <strong>To:</strong> {toLine}
                </div>
              )}
              <div>
                <strong>Subject:</strong> {detail.subject || '(No subject)'}
              </div>
              <div>{timeAgo(detail.date)}</div>
            </div>

            {previewAttachments.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ImageIcon size={16} /> Photos & video
                </strong>
                <div
                  className="email-viewer-previews"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 12,
                    marginTop: 10,
                  }}
                >
                  {previewAttachments.map((att) => {
                    const kind = guessPreviewKind(att);
                    const url = previewUrls[att.id];
                    const failed = previewErrors[att.id];
                    return (
                      <div
                        key={att.id}
                        style={{
                          background: 'var(--surface-2)',
                          borderRadius: 10,
                          overflow: 'hidden',
                          border: '1px solid var(--surface-3)',
                        }}
                      >
                        <div style={{ aspectRatio: '4/3', background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {!url && !failed && <Loader2 size={28} className="spin" style={{ color: 'var(--cyan)' }} />}
                          {failed && (
                            <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', padding: 8 }}>Preview unavailable</span>
                          )}
                          {url && kind === 'image' && (
                            <img
                              src={url}
                              alt={att.filename || 'Attachment'}
                              style={{ width: '100%', height: '100%', objectFit: 'contain', maxHeight: 280, display: 'block' }}
                            />
                          )}
                          {url && kind === 'video' && !videoPlayErrors[att.id] && (
                            <video
                              src={url}
                              controls
                              playsInline
                              preload="auto"
                              style={{ width: '100%', maxHeight: 320, display: 'block' }}
                              onError={() =>
                                setVideoPlayErrors((prev) => ({ ...prev, [att.id]: true }))
                              }
                            />
                          )}
                          {url && kind === 'video' && videoPlayErrors[att.id] && (
                            <div style={{ padding: 12, textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                              <p style={{ margin: '0 0 8px' }}>In-browser playback failed (codec or format).</p>
                              <p style={{ margin: 0 }}>
                                <strong>.mov / HEVC</strong> often won’t play in Chrome — use <strong>Download</strong> below.
                              </p>
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '8px 10px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={att.filename}>
                            {kind === 'video' ? <Film size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : null}
                            {att.filename}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => handleDownload(att)}
                            disabled={downloading === att.id}
                            aria-label={`Download ${att.filename}`}
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {otherAttachments.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <strong>Other attachments</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {otherAttachments.map((att) => (
                    <li key={att.id} style={{ marginBottom: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => handleDownload(att)}
                        disabled={downloading === att.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Download size={14} />
                        {downloading === att.id ? '…' : att.filename}
                        <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>
                          ({Math.round((att.size || 0) / 1024) || '?'} KB)
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!previewAttachments.length && !otherAttachments.length && detail.attachments?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <strong>Attachments</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {detail.attachments.map((att) => (
                    <li key={att.id} style={{ marginBottom: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => handleDownload(att)}
                        disabled={downloading === att.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Download size={14} />
                        {downloading === att.id ? '…' : att.filename}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                flex: 1,
                overflowY: 'auto',
                padding: 12,
                background: 'var(--surface-2)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                fontSize: '0.95rem',
                lineHeight: 1.5,
                minHeight: 80,
              }}
            >
              {bodyDisplay}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
              {isDraft && Boolean(onOpenCompose) && (
                <button type="button" className="btn btn-primary" onClick={openDraftEditor}>
                  <FileEdit size={16} style={{ marginRight: 6 }} />
                  Continue editing draft
                </button>
              )}
              {isReceived && Boolean(onOpenCompose) && (
                <button type="button" className="btn btn-primary" onClick={openReply}>
                  <Reply size={16} style={{ marginRight: 6 }} />
                  Reply
                </button>
              )}
              {isReceived && Boolean(onOpenCompose) && (
                <span className="viewer-readonly-note">
                  <Sparkles size={12} /> Received messages are read-only — replies open a fresh draft.
                </span>
              )}
              <button type="button" className="btn" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
